import axios from "axios";

import { Gallery } from "@/models/gallery.model";
import { GalleryLabel } from "@/models/gallery-label.model";
import { InstitutionDraft } from "@/models/institution.model";
import { Artwork } from "@/models/artwork.model";
import User from "@/models/user.model";


const BASE_API = process.env.REACT_APP_BASE_API;
axios.defaults.withCredentials = +process.env.REACT_APP_LOCAL !== 1;

const errorHandler = (error: any) => {
    const _error = error as any;
    return {
        data: {
            status: false,
            msg: _error.response?.data?.error?.message || _error.response?.data?.message || _error.message,
            error: _error.response?.data?.error || null,
        },
    };
}

const getAuthorizedConfig = (params?: Record<string, unknown>) => {
    const jwt = localStorage.getItem('accessToken');
    return {
        headers: {
            'Authorization': `Bearer ${jwt}`
        },
        ...(params ? { params } : {}),
    };
}

const authorizedRequest = async (func: any, url: string, data?: any) => {
    try {
        const config = getAuthorizedConfig();
        
        if (data) {
            return await func(url, data, config);
        } else {
            return await func(url, config);
        }
    } catch (error) {
        return errorHandler(error);
    }
}

const authorizedGetRequest = async (url: string, params?: Record<string, unknown>) => {
    try {
        return await axios.get(url, getAuthorizedConfig(params));
    } catch (error) {
        return errorHandler(error);
    }
}

export const getUsersRequest = async () => {
    return await authorizedRequest(axios.get, `${BASE_API}/admin/users`);
}

export type AdminUsersDirectorySortBy = "name" | "email" | "createdAt";
export type AdminUsersDirectorySortDirection = "asc" | "desc";

export type AdminUsersDirectoryQuery = {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: AdminUsersDirectorySortBy;
    sortDirection?: AdminUsersDirectorySortDirection;
};

export type AdminUsersDirectoryMeta = {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    search: string | null;
    sortBy: AdminUsersDirectorySortBy;
    sortDirection: AdminUsersDirectorySortDirection;
    paginated: boolean;
};

export type AdminUsersDirectoryResponse = {
    status: boolean;
    users: User[];
    data: {
        items: User[];
    };
    meta: AdminUsersDirectoryMeta;
};

export const getUsersDirectoryRequest = async (query: AdminUsersDirectoryQuery = {}) => {
    const params = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")
    );

    return await authorizedGetRequest(`${BASE_API}/admin/users`, params);
}

export type AdminUserSettingsPayload = {
    curator?: boolean;
    institutionId?: string | null;
    galleryLimit?: number;
};

export const updateAdminUserSettingsRequest = async (
    userId: string,
    payload: AdminUserSettingsPayload
) => {
    return await authorizedRequest(axios.put, `${BASE_API}/admin/users/${userId}`, payload);
}

export const getInstitutionsRequest = async () => {
    return await authorizedRequest(axios.get, `${BASE_API}/admin/institutions`);
}

export const createInstitutionRequest = async (payload: InstitutionDraft) => {
    return await authorizedRequest(axios.post, `${BASE_API}/admin/institutions`, payload);
}

export const updateInstitutionRequest = async (institutionId: string, payload: InstitutionDraft & { slug?: string }) => {
    return await authorizedRequest(axios.patch, `${BASE_API}/admin/institutions/${institutionId}`, payload);
}

export const getSpacesRequest = async () => {
    return await authorizedRequest(axios.get, `${BASE_API}/admin/spaces`);
}

export const getGalleriesRequest = async () => {
    return await authorizedRequest(axios.get, `${BASE_API}/admin/galleries`);
}

export const createGalleryRequest = async (gallery: Gallery) => {
    return await authorizedRequest(axios.post, `${BASE_API}/admin/galleries`, {gallery});
}

export const updateGalleryRequest = async (gallery: Gallery) => {
    return await authorizedRequest(axios.put, `${BASE_API}/admin/galleries/${gallery._id}`, {gallery});

}

export const getDefaultSpaceRequest = async () => {
    try {
        return await axios.get(`${BASE_API}/system/default_space`)
    } catch (error) {
        return errorHandler(error);
    }
}

export const updateDefaultSpaceRequest = async (systemId: string, spaceId: string) => {
    return await authorizedRequest(axios.put, `${BASE_API}/system/default_space/${systemId}`, {spaceId});
}

export const getArtworksRequest = async () => {
    return await authorizedRequest(axios.get, `${BASE_API}/admin/artworks`);
}

export const createArtworkRequest = async (artwork: Artwork) => {
    return await authorizedRequest(axios.post, `${BASE_API}/admin/artworks`, {artwork});
}

export const updateArtworkRequest = async (artwork: Artwork) => {
    return await authorizedRequest(axios.put, `${BASE_API}/admin/artworks/${artwork._id}`, {artwork});
}

export type LegacyBackupRecord = {
    id: string;
    name: string;
    createdAt: string;
};

export type LegacyBackupListResponse = {
    status: true;
    data: {
        items: LegacyBackupRecord[];
    };
    meta: {
        requestId: string;
        legacy: true;
        limit: number;
    };
};

export type LegacyBackupListQuery = {
    from?: Date;
    to?: Date;
    limit?: number;
};

export const getBackups = async (query: LegacyBackupListQuery = {}): Promise<LegacyBackupListResponse> => {
    if (query.from && query.to && query.from.getTime() >= query.to.getTime()) {
        throw new Error("Invalid date range for legacy exports");
    }

    const response = await authorizedGetRequest(`${BASE_API}/admin/backups`, {
        ...(query.from ? { from: query.from.toISOString() } : {}),
        ...(query.to ? { to: query.to.toISOString() } : {}),
        ...(query.limit ? { limit: query.limit } : {}),
    });
    const payload = response?.data;

    if (!payload?.status || !Array.isArray(payload?.data?.items)) {
        throw new Error(payload?.error?.message || payload?.msg || "Legacy export records are unavailable");
    }

    return payload as LegacyBackupListResponse;
};
export const assignMoodToUserRequest = async (spaceId: string, moodIndex: number, users: string[]) => {
    return await authorizedRequest(axios.put, `${BASE_API}/spaces/${spaceId}/assign-moods`, {moodIndex, users});
}

export const unassignMoodFromUserRequest = async (spaceId: string, moodIndex: number, users: string[]) => {
    return await authorizedRequest(axios.put, `${BASE_API}/spaces/${spaceId}/unassign-moods`, {moodIndex, users});
}

export const deleteGalleryRequest = async (id: string) => {
    return await authorizedRequest(axios.delete, `${BASE_API}/admin/galleries/${id}`);
}

export const getGalleryLabelsRequest = async () => {
    return await authorizedRequest(axios.get, `${BASE_API}/admin/gallery-labels`);
}

export const createGalleryLabelRequest = async (payload: Pick<GalleryLabel, "name">) => {
    return await authorizedRequest(axios.post, `${BASE_API}/admin/gallery-labels`, payload);
}

export const updateGalleryLabelRequest = async (labelId: string, payload: Pick<GalleryLabel, "name">) => {
    return await authorizedRequest(axios.put, `${BASE_API}/admin/gallery-labels/${labelId}`, payload);
}

export const deleteGalleryLabelRequest = async (labelId: string) => {
    return await authorizedRequest(axios.delete, `${BASE_API}/admin/gallery-labels/${labelId}`);
}

export type SpatialMigrationAccountStatus = "pending" | "verified" | "seller";

export type SpatialMigrationUser = {
    _id: string;
    name: string;
    email: string;
    roles: string[];
    emailVerified: boolean;
    createdAt?: string;
};

export type SpatialMigrationRecord = {
    _id: string;
    organisation?: string;
    spatialWorldLink?: string;
    spatialWorldLinkText?: string;
    typeOfExperience?: string;
    numberOfWorlds?: string;
    assetExportStatus?: string;
    targetDeadline?: string;
    creatorOrInstitutionCategory?: string;
    createdAt?: string;
    updatedAt?: string;
    user: SpatialMigrationUser | null;
    accountStatus: SpatialMigrationAccountStatus;
    runCount?: number;
    latestRunId?: string | null;
    latestRunStatus?: SpatialMigrationRunStatus | null;
};

export type AdminSpatialMigrationsSortBy = "targetDeadline" | "createdAt" | "name" | "email";
export type AdminSpatialMigrationsSortDirection = "asc" | "desc";

export type AdminSpatialMigrationsQuery = {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: AdminSpatialMigrationsSortBy;
    sortDirection?: AdminSpatialMigrationsSortDirection;
};

export type AdminSpatialMigrationsMeta = {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    search: string | null;
    sortBy: AdminSpatialMigrationsSortBy;
    sortDirection: AdminSpatialMigrationsSortDirection;
    paginated: boolean;
};

export type AdminSpatialMigrationsResponse = {
    status: boolean;
    data: {
        items: SpatialMigrationRecord[];
    };
    meta: AdminSpatialMigrationsMeta;
};

export const getSpatialMigrationsDirectoryRequest = async (query: AdminSpatialMigrationsQuery = {}) => {
    const params = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")
    );

    return await authorizedGetRequest(`${BASE_API}/admin/spatial-migrations`, params);
}

export type SpatialMigrationRunStatus =
    | "DRAFT"
    | "NEEDS_ATTENTION"
    | "IMPORTING_ARTWORKS"
    | "READY_TO_PREVIEW"
    | "READY_TO_APPLY"
    | "APPLYING"
    | "COMPLETED"
    | "CANCELLED"
    | "ARCHIVED";

export type SpatialMigrationSourceSummary = {
    totalObjects: number;
    totalTransforms: number;
    mediaPlacements: number;
    imagePlacements: number;
    modelPlacements: number;
    videoPlacements: number;
    uniqueSourceAssets: number;
    frameMarkers: number;
    framedPlacements: number;
    pedestalMarkers: number;
    pedestalPlacements: number;
    ignoredPortals: number;
};

export type SpatialMigrationRunRecord = {
    id: string;
    status: SpatialMigrationRunStatus;
    intakeRequestId?: string | null;
    targetUserId: string;
    targetUser?: {
        id: string;
        name?: string;
        email?: string;
        roles: string[];
    } | null;
    source: {
        sha256: string;
        sizeBytes: number;
        detectedVersion: number;
        summary: SpatialMigrationSourceSummary;
    };
    target: {
        mode: "existing" | "new";
        galleryId?: string | null;
        newGalleryDraft?: Record<string, unknown> | null;
        lockedAt?: string | null;
    };
    strategy?: "PRESERVE_SPATIAL_LAYOUT" | "FILL_EXISTING_PLACEHOLDERS" | null;
    transform?: {
        scale: number;
        rotationYDegrees: number;
        offsetX: number;
        offsetY: number;
        offsetZ: number;
    };
    previewMoodId?: string | null;
    batchJobIds: string[];
    activeBatchJobId?: string | null;
    targetRevisionFingerprint?: string | null;
    proposalVersion: number;
    counters: {
        total: number;
        selected: number;
        matched: number;
        imported: number;
        proposed: number;
        applied: number;
        skipped: number;
        failed: number;
    };
    firstAppliedAt?: string | null;
    completedAt?: string | null;
    cancelledAt?: string | null;
    archivedAt?: string | null;
    archivedFromStatus?: "COMPLETED" | "CANCELLED" | null;
    createdAt?: string;
    updatedAt?: string;
};

export type SpatialMigrationRunArchivedFilter = "exclude" | "include" | "only";

export type AdminSpatialMigrationRunsQuery = {
    page?: number;
    limit?: number;
    status?: SpatialMigrationRunStatus;
    targetUserId?: string;
    intakeRequestId?: string;
    archived?: SpatialMigrationRunArchivedFilter;
};

export const getSpatialMigrationRunsRequest = async (
    query: AdminSpatialMigrationRunsQuery = {}
) => {
    const params = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")
    );
    return await authorizedGetRequest(`${BASE_API}/admin/spatial-migration-runs`, params);
};

export const getSpatialMigrationRunRequest = async (runId: string) =>
    authorizedGetRequest(`${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}`);

export const createSpatialMigrationRunRequest = async (formData: FormData) =>
    authorizedRequest(axios.post, `${BASE_API}/admin/spatial-migration-runs`, formData);

export type SpatialMigrationMediaType = "image" | "video" | "3dmodel";
export type SpatialMigrationPlacementHint = "framed" | "pedestal" | "hasMetadata" | "unclassified";

export type SpatialMigrationPlacementRecord = {
    id: string;
    runId: string;
    sourceObjectId: string;
    sourceAssetKey: string;
    mediaType: SpatialMigrationMediaType;
    displayFilename: string;
    rawTransform: {
        position: number[];
        quaternion: number[];
        scale: number[];
    };
    derivedTransform: {
        position: number[];
        quaternion: number[];
        scale: number[];
    };
    metadata: {
        title: string;
        description: string;
        creator: string;
    };
    hints: {
        framed: boolean;
        pedestal: boolean;
        hasMetadata: boolean;
        unclassified: boolean;
    };
    selected: boolean;
    skipReason?: string | null;
    matchState: "PENDING" | "AUTO_MATCHED" | "MANUALLY_MATCHED" | "AMBIGUOUS" | "MISSING";
    matchedFilename?: string | null;
    importState: "PENDING" | "QUEUED" | "IMPORTED" | "FAILED" | "SKIPPED";
    batchJobId?: string | null;
    artworkId?: string | null;
    proposalState: "PENDING" | "PROPOSED" | "UNASSIGNED" | "CONFLICT" | "SKIPPED";
    applyState: "PENDING" | "APPLIED" | "FAILED" | "SKIPPED";
    slotTarget?: number | null;
    lastError?: { code: string; message: string } | null;
    appliedAt?: string | null;
    createdAt?: string;
    updatedAt?: string;
};

export type AdminSpatialMigrationPlacementsQuery = {
    page?: number;
    limit?: number;
    mediaType?: SpatialMigrationMediaType;
    selected?: boolean;
    hint?: SpatialMigrationPlacementHint;
    matchState?: SpatialMigrationPlacementRecord["matchState"];
    importState?: SpatialMigrationPlacementRecord["importState"];
    applyState?: SpatialMigrationPlacementRecord["applyState"];
    search?: string;
};

export type UpdateSpatialMigrationPlacementPayload = {
    selected?: boolean;
    skipReason?: string | null;
    title?: string;
    description?: string;
    creator?: string;
};

export const getSpatialMigrationPlacementsRequest = async (
    runId: string,
    query: AdminSpatialMigrationPlacementsQuery = {}
) => {
    const params = Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")
    );
    return authorizedGetRequest(
        `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}/placements`,
        params
    );
};

export const updateSpatialMigrationPlacementRequest = async (
    runId: string,
    sourceObjectId: string,
    payload: UpdateSpatialMigrationPlacementPayload
) => authorizedRequest(
    axios.patch,
    `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}/placements/${encodeURIComponent(sourceObjectId)}`,
    payload
);

export const cancelSpatialMigrationRunRequest = async (runId: string) =>
    authorizedRequest(
        axios.post,
        `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}/cancel`,
        {}
    );

export const archiveSpatialMigrationRunRequest = async (runId: string) =>
    authorizedRequest(
        axios.post,
        `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}/archive`,
        {}
    );

export const unarchiveSpatialMigrationRunRequest = async (runId: string) =>
    authorizedRequest(
        axios.post,
        `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}/unarchive`,
        {}
    );

export const deleteSpatialMigrationRunRequest = async (runId: string) =>
    authorizedRequest(
        axios.delete,
        `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}`
    );

export type SpatialMigrationStrategy =
    | "PRESERVE_SPATIAL_LAYOUT"
    | "FILL_EXISTING_PLACEHOLDERS";

export type SpatialMigrationTargetOption = {
    id: string;
    name: string;
    spaceType: string;
    published: false;
    occupiedSlots: number;
    availableSlots: number;
    strategies: Record<SpatialMigrationStrategy, {
        enabled: boolean;
        reason: string | null;
    }>;
};

export type SpatialMigrationTemplateOption = {
    id: string;
    templateGalleryId: string;
    templateSpaceId: string | null;
    name: string;
    spaceType: string;
    availableSlots: number;
    strategies: SpatialMigrationTargetOption["strategies"];
};

export type SpatialMigrationTargetOptions = {
    selectedPlacementCount: number;
    importedPlacementCount: number;
    galleries: SpatialMigrationTargetOption[];
    newGallery: {
        enabled: boolean;
        reason: string | null;
        usage: number;
        limit: number;
        templates: SpatialMigrationTemplateOption[];
    };
};

export const getSpatialMigrationTargetOptionsRequest = async (runId: string) =>
    authorizedGetRequest(
        `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}/target-options`
    );

export const configureSpatialMigrationTargetRequest = async (
    runId: string,
    payload: {
        targetMode?: "existing";
        targetGalleryId: string;
        strategy: SpatialMigrationStrategy;
    } | {
        targetMode: "new";
        templateGalleryId: string;
        templateSpaceId?: string;
        galleryName: string;
        strategy: SpatialMigrationStrategy;
    }
) => authorizedRequest(
    axios.patch,
    `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}`,
    payload
);

export type SpatialMigrationPreview = {
    revisionFingerprint: string;
    proposalVersion: number;
    gallery: {
        id: string;
        name: string;
        user?: string;
        spaceType: string;
        data?: Record<string, unknown>;
        space: {
            id: string;
            name?: string;
            type: string;
            data: any;
        };
    };
    strategy: SpatialMigrationStrategy;
    placements: Array<{
        sourceObjectId: string;
        slotTarget: number;
        transform: any;
        hints: { framed?: boolean; pedestal?: boolean };
        artwork: {
            id: string;
            title: string;
            type: "image" | "video" | "3dmodel";
            artworkData: any;
        };
    }>;
    emptyPlacements?: Array<{
        sourceObjectId: string;
        title: string;
        slotTarget: number;
        transform: any;
    }>;
    existingWrappers?: Array<{
        id: string;
        slotTarget: number;
        activeArtwork: number;
        transforms?: any;
        artworkTransforms?: Record<string, any>;
        artworks: Array<{
            id: string;
            title: string;
            type: "image" | "video" | "3dmodel";
            artworkData: any;
        }>;
    }>;
    warnings: string[];
};

export const getSpatialMigrationPreviewRequest = async (runId: string) =>
    authorizedGetRequest(
        `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}/preview`
    );

export type SpatialMigrationApplyPlacementResult = {
    sourceObjectId: string;
    title?: string;
    artworkId: string;
    slotTarget: number;
    applyState: "APPLIED" | "FAILED" | "SKIPPED" | "PENDING";
    lastError?: { code: string; message: string } | null;
};

export const applySpatialMigrationRunRequest = async (
    runId: string,
    payload: { expectedFingerprint: string }
) => authorizedRequest(
    axios.post,
    `${BASE_API}/admin/spatial-migration-runs/${encodeURIComponent(runId)}/apply`,
    payload
);
